import {
  InstrumentSparklineData,
  useInstrumentTimeSeriesData,
} from 'features/knowledgeDocumentViewer/hooks/useInstrumentTimeSeriesData'
import { deduplicateDatapoints } from 'features/knowledgeDocumentViewer/utils/instrumentTagUtils'
import React, { useEffect, useState } from 'react'
import styled from 'styled-components'

import { TooltipSparkline } from './TooltipSparkline'
import { Box } from './types'

// ============================================================================
// Types & Interfaces
// ============================================================================

interface MatchedDatapoint {
  id: string
  pi_tag: string
  name: string
  description: string
}

interface InstrumentTimeSeriesTooltipProps {
  box: Box
  matchedDatapoints: MatchedDatapoint[]
}

// ============================================================================
// Constants
// ============================================================================

const TREND_COLOR_POSITIVE = '#6B8CAE'
const TREND_COLOR_NEGATIVE = '#D4A574'

// ============================================================================
// Styled Components
// ============================================================================

const TooltipContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  min-width: 15rem;
  max-width: 20rem;
  background: #0f0f0f;
  border-radius: 0.625rem;
  padding: 0.75rem;
  border: 1px solid #2a2a2a;
  box-shadow: 0 0.5rem 2rem rgba(0, 0, 0, 0.4);
`

const TooltipHeader = styled.div`
  display: flex;
  flex-direction: column;
`

const TooltipLabel = styled.div`
  font-size: 0.875rem;
  font-weight: 600;
  color: #ffffff;
  letter-spacing: -0.01em;
`

const SparklineSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
`

const LoadingContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 3rem;
  padding: 0.5rem;
`

const LoadingText = styled.span`
  font-size: 0.75rem;
  color: #888888;
  font-style: italic;
`

const NoDataMessage = styled.div`
  font-size: 0.75rem;
  color: #666666;
  text-align: center;
  padding: 0.75rem 0.5rem;
`

const DatapointCount = styled.div`
  font-size: 0.625rem;
  color: #666666;
  text-align: center;
  padding-top: 0.25rem;
`

// ============================================================================
// Components
// ============================================================================

function AnimatedLoadingText() {
  const [dots, setDots] = useState('.')

  useEffect(() => {
    const interval = setInterval(() => {
      setDots((prev) => {
        if (prev === '.') return '..'
        if (prev === '..') return '...'
        return '.'
      })
    }, 400)
    return () => clearInterval(interval)
  }, [])

  return <LoadingText>Fetching live data{dots}</LoadingText>
}

export function InstrumentTimeSeriesTooltip({
  box,
  matchedDatapoints,
}: InstrumentTimeSeriesTooltipProps) {
  const dedupedDatapoints = deduplicateDatapoints(matchedDatapoints)
  const datapointIds = dedupedDatapoints.slice(0, 3).map((dp) => dp.id)

  const { sparklineData, isLoading, error } = useInstrumentTimeSeriesData(
    datapointIds,
    datapointIds.length > 0
  )

  return (
    <TooltipContainer>
      <TooltipHeader>
        <TooltipLabel>{box.label || `Box ${box.id}`}</TooltipLabel>
      </TooltipHeader>

      <SparklineSection>
        {isLoading ? (
          <LoadingContainer>
            <AnimatedLoadingText />
          </LoadingContainer>
        ) : error ? (
          <NoDataMessage>Unable to load time series data</NoDataMessage>
        ) : sparklineData.length === 0 ? (
          <NoDataMessage>No recent data available</NoDataMessage>
        ) : (
          sparklineData.map((data: InstrumentSparklineData) => {
            const isPositiveTrend = data.currentValue >= data.previousValue
            const trendColor = isPositiveTrend
              ? TREND_COLOR_POSITIVE
              : TREND_COLOR_NEGATIVE
            return (
              <TooltipSparkline
                key={data.id}
                piTag={data.piTag}
                data={data.data}
                currentValue={data.currentValue}
                previousValue={data.previousValue}
                lastMeasuredAt={data.lastMeasuredAt}
                unit={data.unit}
                color={trendColor}
              />
            )
          })
        )}
      </SparklineSection>

      {dedupedDatapoints.length > 3 && (
        <DatapointCount>
          +{dedupedDatapoints.length - 3} more datapoint
          {dedupedDatapoints.length - 3 > 1 ? 's' : ''}
        </DatapointCount>
      )}
    </TooltipContainer>
  )
}
